import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CloseIcon from '../../../../../assets/svg/x-icon-v2.svg';
import { ACTION_MODAL_TYPES, ActionModal } from '../../../../../DesignSystem/ActionModal';
import { Form, IInputList } from '../../../../../generic-components/Form';
import Heading from '../../../../../generic-components/Heading';
import Icon from '../../../../../generic-components/Icon';
import { UserMessage } from '../../../../../DesignSystem/LiliUserMessage/UserMessage';
import { flattenServerErrorResponse } from '../../../../../helpers/errors.helper';
import { IPayload, useCreateNewRecipientMutation } from '../../../../../hooks/mutations/useCreateNewRecipientMutation';
import { useUpdateRecipientMutation } from '../../../../../hooks/mutations/useUpdateRecipientMutation';
import { useStore } from '../../../../../store';
import { IServerErrorResponse } from '../../../../../types';
import { CREATE_NEW_CLIENT_INPUTS, STYLES, TEXTS } from '../../consts/consts';
import { IInvoiceRecipient } from '../../types/types';
import { formatPhoneNum } from '../../../../../services/phoneNumber.service';

const {
  INVOICE_CREATION: { CREATE_NEW_CLIENT },
} = STYLES;

const {
  INVOICE_CREATION: {
    INVOICE_CLIENT_SECTION: { CREATE_NEW_CLIENT: componentTexts },
  },
} = TEXTS;

interface IProps {
  client?: IInvoiceRecipient;
  handlePostSubmit?: (id: string) => void;
  onClose: () => void;
  initialName?: string;
}

const FORM_INPUT_TYPES = {
  PHONE: 'phone',
  NAME: 'name',
};

export const CreateNewClient = ({ client, handlePostSubmit, onClose, initialName }: IProps) => {
  const [inputsList, setInputsList] = useState<IInputList[]>([]);
  const [payload, setPayload] = useState<IPayload>({ name: '', email: '' });
  const [formError, setFormError] = useState({ key: '', value: '' });
  const {
    user: { customerId },
  } = useStore((state) => state);
  const { t } = useTranslation();
  const [showDiscardPopup, setShowDiscardPopup] = useState<boolean>(false);

  const { mutateAsync: createNewRecipient, isLoading: isCreating } = useCreateNewRecipientMutation({
    customerId,
    payload,
  });

  const { mutateAsync: updateRecipient, isLoading: isUpdating } = useUpdateRecipientMutation({
    customerId,
    payload,
    recipientId: client?.externalId || '',
  });

  const handleFormSubmit = (data: any) => {
    setPayload({
      name: data?.name?.value,
      email: data?.email?.value,
      phone: data?.phone?.value ? formatPhoneNum(data?.phone?.value) : '',
      address: data?.address?.value,
    });
  };

  const handleFormError = (e: IServerErrorResponse) => {
    const { localizedMessage, message } = flattenServerErrorResponse(e);
    setFormError({ key: '', value: localizedMessage || message || 'There is an error, please try again later.' });
    toast.error('Please try again.', {
      position: 'bottom-center',
      autoClose: 3500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
    });
  };
  const handleDeleteSubmitClick = () => {
    setShowDiscardPopup(false);
    onClose();
  };
  useEffect(() => {
    if (payload.name && payload.email) {
      if (client?.externalId) {
        updateRecipient()
          .then((res) => {
            if (handlePostSubmit) handlePostSubmit(res.externalId);
          })
          .catch((e) => {
            handleFormError(e);
          });
      } else {
        createNewRecipient()
          .then((res) => {
            if (handlePostSubmit) handlePostSubmit(res.externalId);
          })
          .catch((e) => {
            handleFormError(e);
          });
      }
    }
  }, [payload]);
  const discardPopup = (
    <ActionModal
      title={t(componentTexts.DISCARD_POPUP.TITLE)}
      description={t(componentTexts.DISCARD_POPUP.SUBTITLE)}
      mainButtonTitle={t(componentTexts.DISCARD_POPUP.DISCARD_BUTTON)}
      cancelButtonTitle={t(componentTexts.DISCARD_POPUP.CANCEL_BUTTON)}
      onSubmit={handleDeleteSubmitClick}
      onCancel={() => setShowDiscardPopup(false)}
      isLoading={false}
      modalType={ACTION_MODAL_TYPES.DESTRUCTIVE}
    />
  );
  // Check if there is initial Value
  useEffect(() => {
    const inputs = [...CREATE_NEW_CLIENT_INPUTS];
    if (client) {
      inputs.forEach((input) => {
        if (client?.[input.TYPE as keyof typeof client]) {
          if (input.TYPE === FORM_INPUT_TYPES.PHONE) {
            const phoneValue = client?.[input.TYPE as keyof typeof client];
            if (phoneValue?.startsWith('+1')) {
              // @ts-ignore
              input.VALUE = client?.[input.TYPE as keyof typeof client]?.replace(/\D/g, '').substring(1);
            } else {
              // @ts-ignore
              input.VALUE = client?.[input.TYPE as keyof typeof client]?.replace(/\D/g, '');
            }
          } else {
            // @ts-ignore
            input.VALUE = client?.[input.TYPE as keyof typeof client];
          }
        } else {
          input.VALUE = '';
        }
      });
    } else {
      inputs.forEach((input) => {
        if (input.TYPE === FORM_INPUT_TYPES.NAME && initialName) {
          input.VALUE = initialName;
        } else {
          input.VALUE = '';
        }
      });
    }
    setInputsList(inputs);
  }, [client]);

  return (
    <UserMessage
      mainWrapperClassName='backdrop-blur-sm'
      contentWrapperClassName='animate-fadeIn'
      onClick={() => {}}
      id='clientEditFormWrapper'
    >
      {showDiscardPopup && discardPopup}
      <div className='w-[568px] min-h-[100px] bg-bw00 rounded-[12px]'>
        <div className={CREATE_NEW_CLIENT.WRAPPER}>
          <div className={CREATE_NEW_CLIENT.HEADER_CONTAINER}>
            <Heading
              title={client?.externalId ? t(componentTexts.TITLE_EDIT) : t(componentTexts.TITLE_CREATE)}
              className={CREATE_NEW_CLIENT.MAIN_TITLE}
            />
            <Icon onClick={() => setShowDiscardPopup(true)} className={CREATE_NEW_CLIENT.CLOSE_ICON} src={CloseIcon} />
          </div>
          <div className={CREATE_NEW_CLIENT.FORM_WRAPPER}>
            <Form
              submitButtonText={
                client?.externalId ? t(componentTexts.SUBMIT_BUTTON_SAVE) : t(componentTexts.SUBMIT_BUTTON_ADD)
              }
              submitButtonClassName={CREATE_NEW_CLIENT.SUBMIT_BUTTON}
              formClassName={CREATE_NEW_CLIENT.FORM}
              inputsList={inputsList}
              onSubmit={handleFormSubmit}
              isLoading={isCreating || isUpdating}
              isSuccess={false}
              isError={!!formError.value}
              error={formError.value}
              onInputsFocus={() => setFormError({ key: '', value: '' })}
              isSubmitValidation
            />
          </div>
        </div>
      </div>
    </UserMessage>
  );
};
